import {
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type HTMLAttributes,
  type MouseEvent,
  type TouchEvent,
} from 'react';
import { flexRender, type Header } from '@tanstack/react-table';
import { MAX_TABLE_COLUMN_WIDTH } from '@kirin/api-client/contracts/user-preferences';
import { cn } from '@/lib/utils';
import {
  TableHead,
  TableRow,
  TableHeader as ShadcnTableHeader,
} from '@/components/ui/table';
import type { ColumnConfig, TableHeaderProps, TableRowData } from '../../types';
import {
  buildColumnSizeStyle,
  measureHeaderMinWidth,
  readColumnSize,
} from '../../utils/columnSizing';
import { HeaderCellContent } from './HeaderCellContent';

interface HeaderDrag {
  fromId: string;
  /** Header the pointer is over right now (highlighted as the drop slot). */
  overId: string | null;
}

/** `dataTransfer` type of a header drag; Firefox starts no drag without data. */
const COLUMN_DRAG_TYPE = 'application/x-column-id';

/** The mouse, or the finger `touchId` (undefined when it is not down). */
const pointerX = (
  event: globalThis.MouseEvent | globalThis.TouchEvent,
  touchId: number | undefined
): number | undefined =>
  'touches' in event
    ? Array.from(event.touches).find(touch => touch.identifier === touchId)
        ?.clientX
    : event.clientX;

/**
 * Drag strip on a header's trailing edge. The width applies on release, so
 * while dragging the strip follows the pointer instead of the column. It
 * tracks the pointer itself, so a move re-renders the strip, not the table.
 * Double-click snaps the column to the narrowest width a drag stops at.
 */
function ColumnResizeHandle<TRow extends TableRowData>({
  header,
  isLast,
}: {
  header: Header<TRow, unknown>;
  /** On the last column the strip stays inside the table, or it widens the scroll area. */
  isLast: boolean;
}) {
  const { column } = header;
  const [offset, setOffset] = useState<number | null>(null);
  // Ends a resize in progress without saving it. Also run on unmount, or the
  // listeners outlive the strip and save into a stale table.
  const stopRef = useRef<(() => void) | null>(null);
  useEffect(() => () => stopRef.current?.(), []);
  // The widths the column can really get: never narrower than its header
  // needs (no word breaks, no hidden control) or its own minimum, never
  // wider than its maximum or what the preference accepts. `cell` is the
  // header, measured while it still has its current width.
  const widthRange = (cell: HTMLElement | null): [number, number] => {
    const { minSize, maxSize } = readColumnSize(column);
    const cap = Math.min(maxSize, MAX_TABLE_COLUMN_WIDTH);
    const floor = Math.max(1, minSize, cell ? measureHeaderMinWidth(cell) : 0);
    return [Math.min(floor, cap), cap];
  };
  const saveWidth = (width: number): void =>
    header.getContext().table.setColumnSizing(old => ({
      ...old,
      [column.id]: width,
    }));
  const startResize = (
    event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>
  ): void => {
    const isTouch = 'touches' in event;
    if (isTouch) {
      // A touch with several fingers is a gesture, not a resize.
      if (event.touches.length > 1) return;
    } else {
      // Left button only: a right-click (or Ctrl-click on macOS) opens a
      // context menu that swallows the mouseup, leaving the resize armed.
      if (event.button !== 0 || event.ctrlKey) return;
      // Default-prevented, or the draggable header starts a column drag.
      event.preventDefault();
    }
    // A gesture whose release never arrived is over now.
    stopRef.current?.();
    const touchId = isTouch ? event.touches[0]?.identifier : undefined;
    const startX = pointerX(event.nativeEvent, touchId) ?? 0;
    const startSize = column.getSize();
    const [floor, cap] = widthRange(event.currentTarget.parentElement);
    let width = startSize;
    const [moveType, endType] = isTouch
      ? (['touchmove', 'touchend'] as const)
      : (['mousemove', 'mouseup'] as const);
    const onMove = (
      moveEvent: globalThis.MouseEvent | globalThis.TouchEvent
    ): void => {
      const x = pointerX(moveEvent, touchId);
      if (x === undefined) return;
      width = Math.round(
        Math.min(cap, Math.max(floor, startSize + x - startX))
      );
      setOffset(width - startSize);
    };
    const stop = (): void => {
      document.removeEventListener(moveType, onMove);
      document.removeEventListener(endType, onEnd);
      document.removeEventListener('touchcancel', stop);
      window.removeEventListener('blur', stop);
      stopRef.current = null;
      setOffset(null);
    };
    const onEnd = (
      endEvent: globalThis.MouseEvent | globalThis.TouchEvent
    ): void => {
      // Another finger lifting does not end this resize.
      if (
        'changedTouches' in endEvent &&
        !Array.from(endEvent.changedTouches).some(
          touch => touch.identifier === touchId
        )
      ) {
        return;
      }
      stop();
      // A click, or a drag that ends where it started, saves nothing: a
      // saved width pins a default the column should follow.
      if (width !== startSize) saveWidth(width);
    };
    document.addEventListener(moveType, onMove);
    document.addEventListener(endType, onEnd);
    // A cancelled touch or a window that loses focus never sends the
    // release; without these the next unrelated release would save.
    document.addEventListener('touchcancel', stop);
    window.addEventListener('blur', stop);
    stopRef.current = stop;
    setOffset(0);
  };
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      // Pointer-only, so hidden from assistive tech: a label would join the
      // header's accessible name and offer an action no keyboard can reach.
      aria-hidden
      data-resizing={offset !== null || undefined}
      onMouseDown={startResize}
      onTouchStart={startResize}
      onDoubleClick={event => {
        const [floor] = widthRange(event.currentTarget.parentElement);
        if (floor !== column.getSize()) saveWidth(floor);
      }}
      className={cn(
        'absolute inset-y-0 z-10 w-2 cursor-col-resize touch-none select-none hover:bg-primary/40 data-[resizing]:bg-primary',
        isLast ? 'right-0' : '-right-1'
      )}
      style={offset ? { transform: `translateX(${offset}px)` } : undefined}
    />
  );
}

export function SingleRowHeader<TRow extends TableRowData>({
  table,
  columns,
  filters,
  onFilterChange,
  sort,
  onSortChange,
  enableSorting,
  enableRowExpansion,
  tableLayout = 'fixed',
  onColumnMove,
}: TableHeaderProps<TRow>) {
  const headers = table.getFlatHeaders();
  // Native drag-and-drop between headers. The ids travel in state rather than
  // in `dataTransfer` (which `dragover` can't read), so the hovered header can
  // highlight as the drop slot.
  const [drag, setDrag] = useState<HeaderDrag | null>(null);
  // React passes events up the component tree, so a drag inside a header's
  // filter popover (portaled out of the `th`) reaches these handlers too.
  // Only a drag on the header itself counts.
  const inHeader = (event: DragEvent<HTMLTableCellElement>): boolean =>
    event.target instanceof Node && event.currentTarget.contains(event.target);
  const dragProps = (id: string): HTMLAttributes<HTMLTableCellElement> =>
    onColumnMove
      ? {
          draggable: true,
          onDragStart: event => {
            if (!inHeader(event)) return;
            if (event.dataTransfer) {
              event.dataTransfer.effectAllowed = 'move';
              event.dataTransfer.setData(COLUMN_DRAG_TYPE, id);
            }
            setDrag({ fromId: id, overId: null });
          },
          onDragOver: event => {
            // Only our own header drags; a file dragged over the table is not.
            if (!drag || !inHeader(event)) return;
            // Required, or the browser refuses the drop.
            event.preventDefault();
            if (drag.overId !== id) setDrag({ ...drag, overId: id });
          },
          onDragLeave: event => {
            // Moving onto the header's own content is still over it.
            const to = event.relatedTarget;
            if (to instanceof Node && event.currentTarget.contains(to)) return;
            setDrag(current =>
              current?.overId === id ? { ...current, overId: null } : current
            );
          },
          onDrop: event => {
            // Anything else (text dropped into a filter box) keeps its default.
            if (!drag || !inHeader(event)) return;
            event.preventDefault();
            if (drag.fromId !== id) onColumnMove(drag.fromId, id);
            setDrag(null);
          },
          onDragEnd: () => setDrag(null),
        }
      : {};

  return (
    <ShadcnTableHeader className="sticky top-0 z-20 border-b bg-muted">
      <TableRow className="border-b overflow-visible !bg-transparent hover:!bg-transparent">
        {enableRowExpansion && (
          <TableHead className="th-divider w-10 px-2" aria-hidden="true" />
        )}
        {headers.map((header, index) => {
          const columnConfig = columns.find(
            col => col.id === header.id
          ) as ColumnConfig<TRow>;

          if (!columnConfig) {
            return (
              <TableHead
                key={header.id}
                style={buildColumnSizeStyle(
                  tableLayout,
                  readColumnSize(header.column)
                )}
                className="th-divider overflow-visible"
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
              </TableHead>
            );
          }

          const headerText =
            typeof header.column.columnDef.header === 'string'
              ? header.column.columnDef.header
              : header.id;

          return (
            <TableHead
              key={header.id}
              style={buildColumnSizeStyle(
                tableLayout,
                readColumnSize(header.column)
              )}
              className={cn(
                'th-divider overflow-visible',
                onColumnMove && 'cursor-grab',
                drag?.fromId === header.id && 'opacity-50',
                drag?.overId === header.id &&
                  drag.fromId !== header.id &&
                  'bg-primary/10 ring-2 ring-inset ring-primary',
                columnConfig.headerClassName
              )}
              data-column-id={header.id}
              {...dragProps(header.id)}
            >
              <HeaderCellContent
                column={columnConfig}
                headerText={headerText}
                filters={filters}
                onFilterChange={onFilterChange}
                sort={sort}
                onSortChange={onSortChange}
                enableSorting={enableSorting ?? false}
              />
              {header.column.getCanResize() && (
                <ColumnResizeHandle
                  header={header}
                  isLast={index === headers.length - 1}
                />
              )}
            </TableHead>
          );
        })}
      </TableRow>
    </ShadcnTableHeader>
  );
}
